/**
 * The pre-season open-to-buy plan as a table: the stored plan terms of each node and period, and
 * the open-to-buy the platform's one ledger arithmetic computes from them.
 *
 * `GET /preseason/period-plans` publishes the TERMS, deliberately not the ledger: "the ledger is
 * arithmetic and the arithmetic has exactly one implementation", behind `POST /preseason/otb`. So
 * the page sends each node's terms there unchanged and joins the answer back by node and period.
 * Nothing here recomputes a position: a requirement, a cover or an open-to-buy is the platform's
 * figure or it is absent.
 *
 * Every term is in units, by contract ("One period's plan terms, in units"). A negative
 * open-to-buy is kept: an over-committed period is a constraint, never a zero.
 */

import type { OtbResponse, PeriodPlanTerms } from '@/api/generated';

/** One node × period: its stored terms and, where the ledger answered, the platform's position. */
export interface PlanLedgerRow {
  readonly nodeId: string;
  readonly period: string;
  readonly openingInventory: number;
  readonly closingInventory: number;
  readonly plannedSalesUnits: number;
  readonly plannedMarkdownUnits: number;
  readonly plannedShrinkUnits: number;
  readonly onOrderUnits: number;
  /** closing + sales + markdown + shrink, as the platform computed it; `null` until it has. */
  readonly requirementUnits: number | null;
  /** opening + on order, as the platform computed it; `null` until it has. */
  readonly coveredUnits: number | null;
  /** requirement − covered, as the platform computed it; `null` until it has. */
  readonly openToBuyUnits: number | null;
  readonly overCommitted: boolean | null;
}

/** The distinct plan nodes, sorted. Pure. */
export function planNodes(plans: readonly PeriodPlanTerms[]): readonly string[] {
  return [...new Set(plans.map((plan) => plan.nodeId))].sort();
}

/**
 * TRANSFORM — the stored terms joined to their node's ledger, by node and period. A node whose
 * ledger has not answered (or failed) keeps its terms and leaves the positions `null`. Sorted by
 * node, then period as the platform writes it. Pure.
 */
export function planLedgerRows(
  plans: readonly PeriodPlanTerms[],
  ledgers: ReadonlyMap<string, OtbResponse>,
): readonly PlanLedgerRow[] {
  return [...plans]
    .sort((a, b) => a.nodeId.localeCompare(b.nodeId) || a.period.localeCompare(b.period))
    .map((plan): PlanLedgerRow => {
      const position = ledgers.get(plan.nodeId)?.positions.find((entry) => entry.period === plan.period);
      return {
        nodeId: plan.nodeId,
        period: plan.period,
        openingInventory: plan.openingInventory,
        closingInventory: plan.closingInventory,
        plannedSalesUnits: plan.plannedSalesUnits,
        plannedMarkdownUnits: plan.plannedMarkdownUnits,
        plannedShrinkUnits: plan.plannedShrinkUnits,
        onOrderUnits: plan.onOrderUnits,
        requirementUnits: position?.requirementUnits ?? null,
        coveredUnits: position?.coveredUnits ?? null,
        openToBuyUnits: position?.openToBuyUnits ?? null,
        overCommitted: position?.isOverCommitted ?? null,
      };
    });
}

/** The figures the OTB page states, all over the WHOLE plan read. */
export interface PlanFigures {
  /** Rows the platform flags over-committed — the count the «over budget» card links to. */
  readonly overCommitted: number;
  readonly lines: number;
  /**
   * The plan's total open-to-buy, the platform's `totalOpenToBuyUnits`, only when ONE node is
   * planned: nodes may nest (a total and its classes), so adding two nodes' totals could count a
   * budget twice. `null` with several nodes, or before the ledger answered.
   */
  readonly totalOpenToBuy: number | null;
  readonly nodes: number;
}

export function planFigures(rows: readonly PlanLedgerRow[], ledgers: ReadonlyMap<string, OtbResponse>): PlanFigures | null {
  if (rows.length === 0) return null;
  const nodes = [...new Set(rows.map((row) => row.nodeId))];
  const only = nodes.length === 1 ? ledgers.get(nodes[0] as string) : undefined;
  return {
    overCommitted: rows.filter((row) => row.overCommitted === true).length,
    lines: rows.length,
    totalOpenToBuy: only === undefined ? null : only.totalOpenToBuyUnits,
    nodes: nodes.length,
  };
}

/** The value the table's «State» column filters on, so a card's link and a hand-made filter agree. */
export function planStateValue(row: Pick<PlanLedgerRow, 'overCommitted'>): string | null {
  return row.overCommitted === null ? null : String(row.overCommitted);
}
